import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, query, state} from "lit/decorators.js";
import type {Job} from "./jobService";
import {JobPreviewRequest, JobService} from "./jobService";
import {schedule} from "../../common/utils/promises";
import {EopImageSpinner, EopOverlaySpinner} from "../../page/elements/spinner";
import {resolve} from "../../container";
import "../../page/elements/input/dropdown";
import {Dictionary} from "../../page/elements/dictionary";
import type {Nullable} from "../../bootstrap/common/types";
import {DROPDOWN_EVENT, type DropdownEvent} from "../../page/elements/input/inputEvents";
import type {DataSelectOption, EopDropdown} from "../../page/elements/input/dropdown";
import {trackingButtonInteract, trackingTextlinkInteract} from "../../tracking/trackingAttributes";
import {UnLitElement, WithEopSlots} from "../../common/elements";


type LabeledUserFilterBase = {
    label: string;
    inverseFilter: boolean;
}

type ConfiguredUserFilter = LabeledUserFilterBase & {
    filter: Filter;
    inverseFilter: false;
};

type OtherUserFilter = LabeledUserFilterBase & {
    inverseFilter: true;
};

type LabeledUserFilter = ConfiguredUserFilter | OtherUserFilter;


type Filter = string[]

const parseLabeledUserFilters = (jsonString: Nullable<string>): LabeledUserFilter[] => {
    if (!jsonString) {
        return [];
    }

    return jsonString.parseAsJSON<{ label: string, value: string }[]>()
        .map(item => ({
            label: item.label,
            filter: item.value.parseCommaSeparated(),
            inverseFilter: false
        }));
};

const parseCommaSeparated = (commaSeparatedString: Nullable<string>): string[] => {
    return commaSeparatedString?.parseCommaSeparated() ?? [];
};

const dataSelectOption = (label: string): DataSelectOption => {
    return {
        label: label,
        value: label,
        preset: false
    };
};


@customElement("eop-job-list-preview")
export class EopJobListPreview extends WithEopSlots(UnLitElement) {

    @property({attribute: "limit", type: Number})
    private limit: number;
    @property({attribute: "filter-job-types", converter: parseCommaSeparated})
    private typeFilter: Filter = [];
    @property({attribute: "filter-job-locations", converter: parseCommaSeparated})
    private locationFilter: Filter = [];
    @property({attribute: "show-job-locations-dropdown"})
    private showLocationsDropdown: boolean;
    @property({attribute: "filter-job-companies", converter: parseCommaSeparated})
    private companiesFilter: Filter = [];
    @property({attribute: "pre-filter-job-titles", converter: parseCommaSeparated})
    private titlePreFilter: Filter = [];
    @property({attribute: "user-filter-job-titles", converter: parseLabeledUserFilters})
    private titleUserFilters: LabeledUserFilter[] = [];

    @state()
    private jobs: Job[] = [];
    @state()
    private offset: number = 0;
    @state()
    private total: Nullable<number> = null;

    @query("eop-image-spinner")
    protected fetchSpinner: EopImageSpinner;
    @query("eop-overlay-spinner")
    protected fetchMoreSpinner: EopOverlaySpinner;

    private currentLocationUserFilter: Nullable<Filter> = null;
    private currentTitleUserFilter: Nullable<LabeledUserFilter> = null;
    private unionOfTitleUserFilters: Filter = [];
    private dictionary: Dictionary;
    private labelOtherTitle: string;

    public constructor(private jobService: JobService = resolve(JobService)) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
        this.labelOtherTitle = this.dictionary.translate("JOB_PREVIEW_DROPDOWN_OTHER_OPTION");
        this.unionOfTitleUserFilters = this.titleUserFilters
            .filter(filter => !filter.inverseFilter)
            .flatMap(filter => filter.filter)
            .distinct();

        void schedule(this.checkOtherJobsAvailability())
            .as("check other jobs");
    }

    private async checkOtherJobsAvailability(): Promise<void> {
        if (this.unionOfTitleUserFilters.length === 0) {
            return;
        }

        const response = await this.jobService.jobPreview(
            new JobPreviewRequest(
                this.typeFilter,
                this.locationFilter,
                this.companiesFilter,
                this.titlePreFilter,
                this.unionOfTitleUserFilters,
                this.limit,
                this.offset,
                true
            )
        );

        if (response.total > 0) {
            const DROPDOWN_OTHER_OPTION: LabeledUserFilter = {
                label: this.labelOtherTitle,
                inverseFilter: true
            };
            this.titleUserFilters = this.titleUserFilters.concat(DROPDOWN_OTHER_OPTION);
        }
    }

    public render(): TemplateResult {
        return html`
            ${this.renderIntroText()}
            ${this.renderDropdowns()}
            <eop-overlay-spinner></eop-overlay-spinner>
            <eop-image-spinner></eop-image-spinner>
            ${this.renderResultsArea()}
            ${this.renderLoadMoreButton()}
        `;
    }

    private renderIntroText(): Nullable<TemplateResult> {
        if (this.total === 0) {
            return null;
        }
        return html`
            <div class="intro-text">
                ${this.resolveSlot("job-list-preview-intro-text")}
            </div>
        `;
    }

    private renderDropdowns(): Nullable<TemplateResult> {
        let locationDropdown = null;
        let titleDropdown = null;
        const placeholder = this.dictionary.translate("JOB_PREVIEW_DROPDOWN_PLACEHOLDER");

        if (this.showLocationsDropdown && this.locationFilter.length > 1) {
            locationDropdown = this.renderDropdown("job-locations",
                this.dictionary.translate("JOB_PREVIEW_LOCATION_DROPDOWN"),
                placeholder,
                this.locationFilter.map(location => dataSelectOption(location))
            );
        }

        if (this.unionOfTitleUserFilters.length > 1) {
            titleDropdown = this.renderDropdown("job-titles",
                this.dictionary.translate("JOB_PREVIEW_TITLE_DROPDOWN"),
                placeholder,
                this.titleUserFilters.map(title => dataSelectOption(title.label))
            );
        }

        if (locationDropdown !== null || titleDropdown !== null) {
            return html`
                <div class="filter-dropdowns">
                    ${locationDropdown}
                    ${titleDropdown}
                </div>`;
        }
        return null;
    }

    private renderDropdown(id: string, label: string, placeholder: string = "", options: DataSelectOption[] = []): Nullable<TemplateResult> {
        return html`
            <div class="dropdown-container">
                <eop-dropdown id=${id} .inputId=${id} .label=${label} .placeholder=${placeholder} .options=${options}></eop-dropdown>
            </div>
        `;
    }

    private renderResultsArea(): Nullable<TemplateResult> {
        if (this.total === 0) {
            return html`
                <div class="empty-results">
                    ${this.resolveSlot("job-list-preview-empty-results-text")}
                </div>
            `;
        }

        if (this.jobs.isEmpty()) {
            return null;
        }

        return html`
            <div class="items">${(this.renderEntries())}</div>`;
    }

    private renderEntries(): TemplateResult[] {
        return this.jobs.map(entry => this.renderEntry((entry)));
    }

    private renderEntry(job: Job): TemplateResult {
        // TODO translate location text
        return html`
            <div class="item">
                <a class="linked-job-title"
                   href=${job.url}
                   target="_blank"
                   rel="noopener"
                   ${trackingTextlinkInteract(job.title, job.url)}
                >${job.title}</a>
                <div class="location">${job.company} in ${job.locations.join(", ")}</div>
            </div>
        `;
    }

    private renderLoadMoreButton(): Nullable<TemplateResult> {
        const fetchMoreLabel = this.dictionary.translate("JOB_PREVIEW_FETCH_MORE");
        if (this.jobs.length < (this.total ?? 0)) {
            return html`
                <button class="load-more-button" @click=${this.fetchMoreHandler} ${trackingButtonInteract(fetchMoreLabel)}>
                    ${fetchMoreLabel}
                </button>
            `;
        }
        return null;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        const locationDropdown = this.querySelector<EopDropdown>("#job-locations");
        locationDropdown?.addEventListener(DROPDOWN_EVENT, (event: Event) => {
            const eventDetails = (event as DropdownEvent).detail;
            const value = eventDetails.value;

            this.currentLocationUserFilter = value === ""
                ? null
                : [value];
            this.offset = 0;
            void schedule(this.fetchMoreSpinner.spinWhile(this.fetch()))
                .as("filter by location");
        });

        const titleDropdown = this.querySelector<EopDropdown>("#job-titles");
        titleDropdown?.addEventListener(DROPDOWN_EVENT, (event: Event) => {
            const eventDetails = (event as DropdownEvent).detail;
            const value = eventDetails.value;

            this.currentTitleUserFilter = this.titleUserFilters.find(filter => filter.label === value) ?? null;
            this.offset = 0;
            void schedule(this.fetchMoreSpinner.spinWhile(this.fetch()))
                .as("filter by title");
        });

        void schedule(this.fetchSpinner.spinWhile(this.fetch()))
            .as("fetch");
    }

    private fetchMoreHandler(): void {
        this.offset += this.limit;
        void schedule(this.fetchMoreSpinner.spinWhile(this.fetch(true)))
            .as("more");
    }

    private async fetch(loadMore: boolean = false): Promise<void> {
        const invertTitleFilter = this.currentTitleUserFilter?.inverseFilter ?? false;
        const titleUserFilter = invertTitleFilter
            ? this.unionOfTitleUserFilters
            : (this.currentTitleUserFilter as ConfiguredUserFilter)?.filter;

        const response = await this.jobService.jobPreview(
            new JobPreviewRequest(
                this.typeFilter,
                this.currentLocationUserFilter ?? this.locationFilter,
                this.companiesFilter,
                this.titlePreFilter,
                titleUserFilter ?? [],
                this.limit,
                this.offset,
                invertTitleFilter
            )
        );

        this.jobs = loadMore
            ? this.jobs.concat(response.jobs)
            : response.jobs;

        this.total = response.total;
    }
}